import React, {useEffect, useState} from 'react';
import {Alert, Button, Card, Col, Container, Form, Row, Spinner} from 'react-bootstrap';
import {useAuth} from '../context/auth.context';
import {UserService} from '../../service/users.service';
import {CreateUserDto, User} from '../../service/types/types';

type PerfilForm = {
    name: string;
    apellido_paterno: string;
    apellido_materno: string;
    fecha_nacimiento: string;
    sexo: string;
    phone: string;
    email: string;
    direccion: string;
    comuna: string;
    provincia: string;
    region: string;
    estado_civil: string;
    nivel_educacion: string;
};

const toForm = (user: User): PerfilForm => ({
    name: user.name || '',
    apellido_paterno: user.apellido_paterno || '',
    apellido_materno: user.apellido_materno || '',
    fecha_nacimiento: user.fecha_nacimiento || '',
    sexo: user.sexo || '',
    phone: user.phone || '',
    email: user.email || '',
    direccion: user.direccion || '',
    comuna: user.comuna || '',
    provincia: user.provincia || '',
    region: user.region || '',
    estado_civil: user.estado_civil || '',
    nivel_educacion: user.nivel_educacion || '',
});

export default function MiPerfil() {
    const {getTokenData} = useAuth();
    const rut = getTokenData()?.rut as string | undefined;

    const [user, setUser] = useState<User | null>(null);
    const [form, setForm] = useState<PerfilForm | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!rut) {
            setLoading(false);
            return;
        }
        UserService.findOne(rut)
            .then((data) => {
                if (data) {
                    setUser(data);
                    setForm(toForm(data));
                }
            })
            .finally(() => setLoading(false));
    }, [rut]);

    const handleChange = (field: keyof PerfilForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => (prev ? {...prev, [field]: e.target.value} : prev));
    };

    const handleSelectChange = (field: keyof PerfilForm) => (e: React.ChangeEvent<HTMLSelectElement>) => {
        setForm((prev) => (prev ? {...prev, [field]: e.target.value} : prev));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        if (!user || !form || !rut) return;

        if (!form.name.trim()) {
            setError('El nombre no puede quedar vacío.');
            return;
        }
        if (newPassword || confirmPassword) {
            if (newPassword.length < 4) {
                setError('La nueva contraseña debe tener al menos 4 caracteres.');
                return;
            }
            if (newPassword !== confirmPassword) {
                setError('Las contraseñas no coinciden.');
                return;
            }
        }

        const dto: CreateUserDto = {
            rut: user.rut,
            name: form.name.trim(),
            apellido_paterno: form.apellido_paterno,
            apellido_materno: form.apellido_materno,
            fecha_nacimiento: form.fecha_nacimiento || undefined,
            sexo: form.sexo,
            phone: form.phone,
            email: form.email,
            direccion: form.direccion,
            comuna: form.comuna,
            provincia: form.provincia,
            region: form.region,
            estado_civil: form.estado_civil,
            nivel_educacion: form.nivel_educacion,
            password: newPassword || undefined,
            // Campos administrativos/laborales: se mantienen intactos, no editables desde "Mi Perfil".
            numero_cargas: user.numero_cargas,
            afp: user.afp,
            no_calzado: user.no_calzado,
            talla: user.talla,
            fecha_ingreso: user.fecha_ingreso,
            sueldo: user.sueldo,
            bonos: user.bonos,
            cargo: user.cargo,
            jornada: user.jornada,
            contrato: user.contrato,
            prevision: user.prevision,
            is_active: user.is_active,
            localidad: user.localidad?.id ?? '',
            turno: user.turno,
        };

        setSaving(true);
        try {
            const updated = await UserService.update(rut, dto);
            if (updated) {
                setUser(updated);
                setForm(toForm(updated));
            }
            setNewPassword('');
            setConfirmPassword('');
            setSuccess(true);
        } catch (err) {
            setError('No se pudieron guardar los cambios. Intenta nuevamente.');
        } finally {
            setSaving(false);
        }
    };

    if (!rut) {
        return (
            <Container className="py-4">
                <Alert variant="warning">Debes iniciar sesión para ver tu perfil.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" />
            </Container>
        );
    }

    if (!user || !form) {
        return (
            <Container className="py-4">
                <Alert variant="danger">No se pudo cargar tu información. Intenta nuevamente más tarde.</Alert>
            </Container>
        );
    }

    return (
        <div className="w-100 min-vh-100 bg-light p-3">
            <Container className="p-4 bg-white shadow rounded" style={{maxWidth: '760px'}}>
                <h2 className="mb-1">Mi Perfil</h2>
                <p className="text-muted mb-4">
                    {user.name} &middot; RUT {user.rut}
                </p>

                {success && (
                    <Alert variant="success" onClose={() => setSuccess(false)} dismissible>
                        Tus datos se actualizaron correctamente.
                    </Alert>
                )}
                {error && <Alert variant="danger">{error}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nombre *</Form.Label>
                                <Form.Control value={form.name} onChange={handleChange('name')} required />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Apellido Paterno</Form.Label>
                                <Form.Control value={form.apellido_paterno} onChange={handleChange('apellido_paterno')} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Apellido Materno</Form.Label>
                                <Form.Control value={form.apellido_materno} onChange={handleChange('apellido_materno')} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Fecha de Nacimiento</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={form.fecha_nacimiento}
                                    onChange={handleChange('fecha_nacimiento')}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Sexo</Form.Label>
                                <Form.Select value={form.sexo} onChange={handleSelectChange('sexo')}>
                                    <option value="">Seleccione</option>
                                    <option value="M">Masculino</option>
                                    <option value="F">Femenino</option>
                                    <option value="O">Otro</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Teléfono</Form.Label>
                                <Form.Control value={form.phone} onChange={handleChange('phone')} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <Form.Control type="email" value={form.email} onChange={handleChange('email')} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label>Dirección</Form.Label>
                        <Form.Control value={form.direccion} onChange={handleChange('direccion')} />
                    </Form.Group>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Comuna</Form.Label>
                                <Form.Control value={form.comuna} onChange={handleChange('comuna')} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Provincia</Form.Label>
                                <Form.Control value={form.provincia} onChange={handleChange('provincia')} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Región</Form.Label>
                                <Form.Control value={form.region} onChange={handleChange('region')} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Estado Civil</Form.Label>
                                <Form.Control value={form.estado_civil} onChange={handleChange('estado_civil')} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nivel Educación</Form.Label>
                                <Form.Control value={form.nivel_educacion} onChange={handleChange('nivel_educacion')} />
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr className="my-4" />
                    <h5 className="mb-3">Cambiar contraseña</h5>
                    <p className="text-muted small">Déjalo en blanco si no quieres cambiar tu contraseña actual.</p>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nueva contraseña</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    autoComplete="new-password"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Confirmar contraseña</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    autoComplete="new-password"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Button type="submit" variant="primary" className="w-100 mt-2" disabled={saving}>
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" /> Guardando...
                            </>
                        ) : (
                            'Guardar cambios'
                        )}
                    </Button>
                </Form>
            </Container>
        </div>
    );
}
